import React, { FC } from 'react';
import {
  Button,
  Icon,
  Tooltip,
  useDisclosure,
  RemoveAlertDialog,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import useflowsProductBasketStore from '@flows/src/store/zustand/flows-products/flowsProductBasketStore';
import FinishBasketModal from '@flows/src/components/flow-products/quotes/basket/FinishBasketModal';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import { useParty } from '@parties/src/features/detail/api/getParty';
import { FinishBasketPartyCreationModal } from '@flows/src/components/flow-products/quotes/basket/actions/anva-party-creation-modal/FinishBasketPartyCreationModal';
import { useBasketCalculation } from '@flows/src/components/flow-products/quotes/basket/BasketCalculationProvider';
import FlowProductsCalculateError from '@flows/src/pages/flow-products/FlowProductsCalculateError';
import MaatwerkConfirmationModal from '@flows/src/components/flow-products/quotes/basket/MaatwerkConfirmationModal';
import MaatwerkLoadingModal from '@flows/src/components/flow-products/quotes/basket/MaatwerkLoadingModal';
import {
  hasAnyMaatwerkItems,
  areAllMaatwerkItemsRequested,
  isMaatwerkPackage,
  isMaatwerkQuote,
} from '@flows/src/utils/basketCalculations';
import { requestMaatwerkOfferte } from '@flows/src/api/anva-flows/requestMaatwerkOfferte';
import {
  getJobResult,
  AnvaJobErrorResult,
} from '@flows/src/api/anva-flows/getAnvaQuotesV2';
import { showMaatwerkErrorToast } from '@flows/src/utils/maatwerkErrorHandling';
import { buildCalculationPayload } from '@flows/src/utils/basketCalculationPayload';

interface MaatwerkPromise {
  resolve: () => void;
  reject: (error?: Error) => void;
}

interface Props {
  sessionId: string;
}

const BasketFinishButton: FC<Props> = ({ sessionId }) => {
  const { t } = useTranslation();
  // sessionId is already provided via props
  const { data: sessionData } = useFlowsSession(sessionId);
  const partyId = sessionData?.prospect?.id || '';
  const { data: partyData, isSuccess: isPartyDataSuccess } = useParty(partyId);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: isPartyModalOpen,
    onClose: onPartyModalClose,
    onOpen: onPartyModalOpen,
  } = useDisclosure();
  const {
    isOpen: isErrorModalOpen,
    onClose: onErrorModalClose,
    onOpen: onErrorModalOpen,
  } = useDisclosure();

  const {
    isOpen: isMaatwerkModalOpen,
    onClose: onMaatwerkModalClose,
    onOpen: onMaatwerkModalOpen,
  } = useDisclosure();

  const {
    isOpen: isMaatwerkLoadingOpen,
    onClose: onMaatwerkLoadingClose,
    onOpen: onMaatwerkLoadingOpen,
  } = useDisclosure();

  const packages = useflowsProductBasketStore((state) => state.packages);
  const insurances = useflowsProductBasketStore((state) => state.insurances);
  const isBasketValidated = useflowsProductBasketStore(
    (state) => state.isBasketValidated,
  );

  const { calculate, isCalculating, error } = useBasketCalculation();

  // New disabled state logic: disabled only if no packages AND no insurances
  const isDisabled = packages.length === 0 && insurances.length === 0;

  // Helpers to mark quotes/packages as requested in the store
  const updateInsurance = useflowsProductBasketStore(
    (state) => state.updateInsurance,
  );
  const updatePackage = useflowsProductBasketStore(
    (state) => state.updatePackage,
  );

  const requestAllPendingMaatwerk = async () => {
    onMaatwerkLoadingOpen();
    try {
      // First ensure ANVA party exists before any maatwerk requests
      const hasAnvaRef = partyData?.external_refs?.some(
        (ref) => ref.company === 'anva',
      );

      if (!hasAnvaRef) {
        // Show party creation modal and wait for completion
        onPartyModalOpen();
        return new Promise<void>((resolve, reject) => {
          // Store the resolve/reject to be called after party creation
          (
            window as unknown as Record<string, unknown>
          ).__basketMaatwerkPromise = {
            resolve,
            reject,
          };
        });
      }

      // Continue with maatwerk requests if party exists
      await performMaatwerkRequests();
    } finally {
      onMaatwerkLoadingClose();
    }
  };

  const performMaatwerkRequests = async () => {
    const POLL_INTERVAL = 3000;
    const POLL_TIMEOUT = 60000;

    const pendingInsurances = insurances.filter(
      (q) => isMaatwerkQuote(q) && !q.maatwerkRequested,
    );
    const pendingPackages = packages.filter(
      (p) => isMaatwerkPackage(p) && !p.maatwerkRequested,
    );

    // Handle standalone insurances
    for (const quote of pendingInsurances) {
      const payload = {
        chosen_insurance_packages: [],
        chosen_insurance_policies: [
          {
            anva_product_id: quote.anva_product_id,
            index: quote.index,
            coverages: quote.coverages
              .filter(({ selected }) => selected)
              .map(({ code }) => Number(code)),
            due_date: quote.start_date,
            payment_interval: quote.payment_interval ?? null,
          },
        ],
      };
      try {
        const { job_id } = await requestMaatwerkOfferte({
          sessionId,
          payload,
        });

        // Poll job
        const start = Date.now();
        let completed = false;
        while (!completed) {
          if (Date.now() - start > POLL_TIMEOUT) {
            throw new Error('MAATWERK_EXPORT_TIMEOUT');
          }
          // eslint-disable-next-line no-await-in-loop
          const jobRes = await getJobResult(job_id);
          if (jobRes.status === 'SUCCEEDED') {
            completed = true;
          } else if (jobRes.status === 'FAILED') {
            // Extract error details from the job result
            const result = jobRes.result as AnvaJobErrorResult | undefined;
            const title = result?.title || '';
            const message = result?.message || '';

            // Create a structured error with title and message
            const errorDetails = {
              title: title || 'Maatwerkofferte export gefaald',
              message:
                message ||
                'Er is een fout opgetreden bij het exporteren van de maatwerkofferte',
              jobResult: jobRes,
            };

            throw new Error(JSON.stringify(errorDetails));
          } else {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((res) => setTimeout(res, POLL_INTERVAL));
          }
        }
        updateInsurance(quote.anva_product_id, quote.index, {
          maatwerkRequested: true,
        });
      } catch (err) {
        showMaatwerkErrorToast(err, t);
      }
    }

    // Handle packages (single request per package)
    for (const pkg of pendingPackages) {
      try {
        const payload = {
          chosen_insurance_packages: [
            {
              name: pkg.name,
              code: pkg.code,
              payment_interval: pkg.payment_interval ?? null,
              chosen_insurance_policies: pkg.quotes.map((q) => ({
                anva_product_id: q.anva_product_id,
                index: q.index,
                coverages: q.coverages
                  .filter(({ selected }) => selected)
                  .map(({ code }) => Number(code)),
              })),
            },
          ],
          chosen_insurance_policies: [],
        };

        const { job_id } = await requestMaatwerkOfferte({
          sessionId,
          payload,
        });

        // Poll job
        const start = Date.now();
        let completed = false;
        while (!completed) {
          if (Date.now() - start > POLL_TIMEOUT) {
            throw new Error('MAATWERK_EXPORT_TIMEOUT');
          }
          // eslint-disable-next-line no-await-in-loop
          const jobRes = await getJobResult(job_id);
          if (jobRes.status === 'SUCCEEDED') {
            completed = true;
          } else if (jobRes.status === 'FAILED') {
            // Extract error details from the job result
            const result = jobRes.result as AnvaJobErrorResult | undefined;
            const title = result?.title || '';
            const message = result?.message || '';

            // Create a structured error with title and message
            const errorDetails = {
              title: title || 'Maatwerkofferte export gefaald',
              message:
                message ||
                'Er is een fout opgetreden bij het exporteren van de maatwerkofferte',
              jobResult: jobRes,
            };

            throw new Error(JSON.stringify(errorDetails));
          } else {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((res) => setTimeout(res, POLL_INTERVAL));
          }
        }
        updatePackage(pkg.code, { maatwerkRequested: true });
      } catch (err) {
        showMaatwerkErrorToast(err, t);
      }
    }
  };

  const handleFinishWorkflow = () => {
    // is party an anva party?
    const hasAnvaRef = partyData?.external_refs?.some(
      (ref) => ref.company === 'anva',
    );
    if (hasAnvaRef) {
      onOpen();
    } else {
      onPartyModalOpen();
    }
  };

  const handleValidation = async () => {
    try {
      const calculationPayload = buildCalculationPayload(packages, insurances);
      await calculate(calculationPayload);
    } catch (calcError) {
      console.error('Calculation error:', calcError);
      onErrorModalOpen();
    }
  };

  const continueAfterMaatwerkCheck = async () => {
    // Perform validation first when required
    if (packages.length > 0 && !isBasketValidated) {
      await handleValidation();
      return;
    }

    // Nothing left to validate – finish the workflow
    handleFinishWorkflow();
  };

  const handleClick = async () => {
    // 1️⃣ Check for pending maatwerk requests right away
    const hasMaatwerk = hasAnyMaatwerkItems();
    const allRequested = areAllMaatwerkItemsRequested();

    if (hasMaatwerk && !allRequested) {
      onMaatwerkModalOpen();
      return;
    }

    // 2️⃣ No pending maatwerk ➜ proceed with validation / finish
    await continueAfterMaatwerkCheck();
  };

  const handleMaatwerkConfirm = async () => {
    onMaatwerkModalClose();
    await requestAllPendingMaatwerk();
    await continueAfterMaatwerkCheck();
  };

  const handlePartyCreationFinish = async () => {
    onPartyModalClose();
    // Resolve the pending promise and continue with maatwerk requests
    const promise = (window as unknown as Record<string, unknown>)
      .__basketMaatwerkPromise as MaatwerkPromise | undefined;
    if (promise) {
      delete (window as unknown as Record<string, unknown>)
        .__basketMaatwerkPromise;
      try {
        await performMaatwerkRequests();
        promise.resolve();
      } catch (err) {
        promise.reject(err instanceof Error ? err : new Error(String(err)));
      }
    }
    // Continue with the normal flow
    onOpen();
  };

  const handleSkipFinish = () => {
    onMaatwerkModalClose();
    handleFinishWorkflow();
  };

  // Calculate pending maatwerk count for the modal
  const getPendingMaatwerkCount = (): number => {
    const maatwerkInsurances = insurances.filter((quote) =>
      isMaatwerkQuote(quote),
    );
    const maatwerkPackages = packages.filter((pkg) => isMaatwerkPackage(pkg));

    const pendingInsurances = maatwerkInsurances.filter(
      (quote) => !quote.maatwerkRequested,
    ).length;
    const pendingPackages = maatwerkPackages.filter(
      (pkg) => !pkg.maatwerkRequested,
    ).length;

    return pendingInsurances + pendingPackages;
  };

  // Auto-open error modal when there's a validation error
  React.useEffect(() => {
    if (error && !isCalculating) {
      onErrorModalOpen();
    }
  }, [error, isCalculating]);

  // After basket validation succeeds, finish the flow automatically
  React.useEffect(() => {
    if (isBasketValidated && packages.length > 0 && !isCalculating && !error) {
      handleFinishWorkflow();
    }
  }, [isBasketValidated, packages.length, isCalculating, error]);

  const getTooltipLabel = () => {
    if (isDisabled) {
      return t('FLOWS_PRODUCTS_BASKET_ACTIONS_VALIDATE_EMPTY_TOOLTIP');
    }
    return '';
  };

  return (
    <>
      {partyData && (
        <FinishBasketPartyCreationModal
          isOpen={isPartyModalOpen}
          party={partyData}
          onClose={onPartyModalClose}
          onFinish={handlePartyCreationFinish}
          sessionId={sessionId}
        />
      )}
      <FinishBasketModal
        isOpen={isOpen}
        onClose={onClose}
        sessionId={sessionId}
      />
      <RemoveAlertDialog
        isOpen={isErrorModalOpen}
        onClose={onErrorModalClose}
        onConfirm={onErrorModalClose}
        title={t('Calculation failed')}
        hideCancelButton
        confirmButtonText={t('CLOSE')}
        size="2xl"
      >
        <FlowProductsCalculateError error={error || undefined} />
      </RemoveAlertDialog>
      <MaatwerkConfirmationModal
        isOpen={isMaatwerkModalOpen}
        onClose={onMaatwerkModalClose}
        onConfirm={handleMaatwerkConfirm}
        onSkip={handleSkipFinish}
        pendingMaatwerkCount={getPendingMaatwerkCount()}
      />
      <MaatwerkLoadingModal
        isOpen={isMaatwerkLoadingOpen}
        onClose={onMaatwerkLoadingClose}
      />
      <Tooltip isDisabled={!isDisabled} label={getTooltipLabel()}>
        <Button
          isDisabled={isDisabled && isPartyDataSuccess}
          onClick={handleClick}
          isLoading={isCalculating}
          rightIcon={<Icon name="GeneralActionsArrowRight" />}
        >
          {t('FINISH')}
        </Button>
      </Tooltip>
    </>
  );
};

export default BasketFinishButton;
